import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";

const faqItems = [
  {
    q: "Can this be deployed across multiple markets?",
    a: "Yes. Bellhopt is built for multi-market execution.",
  },
  {
    q: "What is the minimum lead time?",
    a: "24 hours.",
  },
  {
    q: "Do you support enterprise portfolios?",
    a: "Yes. Bellhopt supports any size portfolio, including 1,000+ homes.",
  },
  {
    q: "Do you require a PMS integration?",
    a: "No. Integrations help streamline onboarding, but manual onboarding is available if needed.",
  },
];

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">{title}</h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">{children}</div>
    </section>
  );
}

function ForPropertyManagersPage() {
  const title = "For Property Managers | Arrival-Ready Amenities Without Ops Burden";
  const description =
    "Bellhopt helps property managers deliver hotel-level convenience without adding workload. Bellhopt Concierge stocks and organizes homes with a 24-hour lead time.";
  const canonical = "https://bellhopt.com/for/property-managers/";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "A Concierge Layer For Property Managers, Built For Scale",
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://bellhopt.com/" },
        { "@type": "ListItem", position: 2, name: "For Property Managers", item: canonical },
      ],
    },
  ];

  return (
    <div className="w-full min-h-screen bg-[#F9FAFB] pt-[72px] font-segoe">
      <SeoHead
        title={title}
        description={description}
        canonical="/for/property-managers/"
        robots="index,follow"
        ogTitle="For Property Managers"
        ogDescription="A concierge execution layer that scales arrival-ready guest experience across properties and markets."
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto py-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            A Concierge Layer For Property Managers, Built For Scale
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            Amenities have become a booking and review driver, but most operators cannot execute them consistently across distributed homes. Bellhopt provides a concierge layer that delivers, stocks, and organizes to an arrival-ready standard without adding operational burden.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started With Bellhopt</a>
            <Link to="/integrations" className={SECONDARY_CTA}>See Integrations</Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            24-hour lead time required. Built for any size portfolio, including 1,000+ homes.
          </p>
        </section>

        <div className="mt-6 space-y-6">
          <Section id="real-problem" title="The Real Problem Is Not Amenities, It Is Execution">
            <p>Buying items is easy. Delivering a consistent experience across:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>different cities</li>
              <li>different access methods</li>
              <li>different arrival times</li>
              <li>different guest expectations</li>
            </ul>
            <p className="mt-4">is the hard part.</p>
            <p className="mt-2">Bellhopt is designed to make execution repeatable.</p>
          </Section>

          <Section id="what-bellhopt-enables" title="What Bellhopt Enables">
            <ul className="list-disc pl-6">
              <li>A consistent arrival-ready standard</li>
              <li>A guest-friendly ordering flow</li>
              <li>A concierge execution layer that handles the work</li>
              <li>Clear policies that reduce exception chaos</li>
            </ul>
          </Section>

          <Section id="rollout" title="Rollout Framework">
            <ol className="list-decimal pl-6 space-y-4">
              <li>
                <strong>Onboard properties</strong>
                <p>Integration or manual onboarding.</p>
              </li>
              <li>
                <strong>Standardize the experience</strong>
                <p>Define what "arrival-ready" means for your portfolio.</p>
              </li>
              <li>
                <strong>Launch guest flow</strong>
                <p>Make ordering simple and aligned to the stay.</p>
              </li>
              <li>
                <strong>Execute consistently</strong>
                <p>Bellhopt Concierge delivers, stocks, and organizes.</p>
              </li>
            </ol>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Launch Across Your Portfolio
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              A concierge layer that scales arrival-ready guest experience without adding operational burden.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>Get Started</a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>Talk To Sales</a>
            </div>
          </section>

          <Section id="why-commercially" title="Why This Matters Commercially">
            <p>Operators compete locally. Guests compare listings side by side.</p>
            <p className="mt-4">An arrival-ready standard:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>improves perceived quality</li>
              <li>reduces friction in the first hour</li>
              <li>supports better reviews and repeat stays</li>
              <li>helps properties win bookings against nearby alternatives</li>
            </ul>
          </Section>

          <Section id="faq" title="FAQ">
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details key={item.q} className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]">
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">{item.q}</summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">Related Pages</h2>
            <ul className="mt-4 list-disc pl-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              <li><Link className="text-primary" to="/for/enterprise-property-managers">Enterprise property managers</Link></li>
              <li><Link className="text-primary" to="/for/mid-market-property-managers">Mid-market property managers</Link></li>
              <li><Link className="text-primary" to="/integrations">Integrations</Link></li>
              <li><Link className="text-primary" to="/how-it-works">How it works</Link></li>
              <li><Link className="text-primary" to="/pricing">Pricing</Link></li>
              <li><Link className="text-primary" to="/pre-arrival-groceries">Pre-arrival groceries</Link></li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default ForPropertyManagersPage;
